/**
 * The audit trail, as GET /history serves it. Mirrors the DTOs in the allocation service's
 * AuditService — see README-history.md there for the contract this is written against.
 *
 * The shape is the same for every entity, because the service reports a **diff and not a
 * snapshot**: one revision, and the fields that actually moved in it. That is what lets one
 * component render the history of a class and of an instructor, and what makes a new field on
 * either appear here with no change on this side. Nothing in this file may grow a per-entity
 * branch without giving that up.
 */

/** One entity whose history this caller may read, as GET /history lists them. */
export interface AuditableEntityView {
  /** The URL segment, matching the entity's own resource path: `class`, `instructor`. */
  entity: string;
  /** The Java type, for display only — never send this back. */
  type: string;
}

/** What happened to the row in a revision. `DEL` carries no field changes; see FieldChange. */
export type RevisionType = 'ADD' | 'MOD' | 'DEL';

/**
 * Which components of a FieldChange are in play. Switch on this rather than testing for null:
 * every component is always present, so `from: null` means the field had no value and cannot be
 * told apart from "not applicable" any other way.
 */
export type FieldChangeKind = 'SCALAR' | 'ENUM' | 'REFERENCE' | 'COLLECTION';

/**
 * A related row reduced to what a history needs. The `id` is always there; the `label` is read
 * only when the service already had the row loaded, so a null label is a display problem and
 * never a missing reference.
 *
 * Skill and CustomFieldValue have no field the service can label from (it looks for `name`,
 * `title`, `key`, `code`, `sku`, `fullname`, in that order) and always come back id-only. A skill
 * is resolvable from the instructor's own record instead, because a skill is never removed - see
 * skillRefLabel in model/Instructor.
 */
export interface Ref {
  id: number;
  label?: string | null;
}

/** A scalar as it travels: text, number and boolean as-is, dates as ISO 8601 strings. */
export type ScalarValue = string | number | boolean | null;

export interface FieldChange {
  /** The entity's own field name. Labels are ours — see fieldLabel. */
  field: string;
  kind: FieldChangeKind;
  /** Used by everything except COLLECTION. */
  from?: ScalarValue | Ref;
  to?: ScalarValue | Ref;
  /** COLLECTION only: what joined and what left, compared as sets. */
  added?: (Ref | ScalarValue)[] | null;
  removed?: (Ref | ScalarValue)[] | null;
}

export interface RevisionEntry {
  revision: number;
  changedAt: string;
  /**
   * A principal, `system` for a real change with no user behind it, or null when the change
   * predates attribution — including the synthetic baseline revision the audit schema created.
   * Render it with ChangeAuthor, which keeps the three apart.
   */
  username?: string | null;
  type: RevisionType;
  /**
   * Empty on a `DEL`, which records the identity and nothing else, and empty as well on a save
   * where no audited field moved. Both are events worth showing; neither is a bug.
   */
  changes: FieldChange[];
}

/** One row's history, newest revision first. */
export interface EntityHistory {
  entity: string;
  id: number;
  page: number;
  size: number;
  /** There is at least one more revision after this page. The service knows because it reads
   * `size + 1` and diffs the oldest revision on the page against the extra one. */
  hasMore: boolean;
  revisions: RevisionEntry[];
}

/** One entity type a revision touched, in the feed. */
export interface TouchedEntity {
  /**
   * The slug to follow, or null when the type is audited but either not registered for history
   * or not readable by this caller — the two are deliberately indistinguishable. A null `entity`
   * always comes with an empty `ids`.
   */
  entity?: string | null;
  type: string;
  ids: number[];
}

export interface FeedEntry {
  revision: number;
  changedAt: string;
  username?: string | null;
  touched: TouchedEntity[];
}

/** What changed recently across everything, newest revision first. */
export interface HistoryFeed {
  page: number;
  size: number;
  hasMore: boolean;
  entries: FeedEntry[];
}

/** Filters GET /history/feed accepts. All optional; all narrow rather than widen. */
export interface HistoryFeedParams {
  /** Only changes by this principal. `system` selects the ones no user caused. */
  username?: string;
  /** Only revisions touching this entity type, by slug. */
  entity?: string;
  /** ISO 8601 instants, inclusive. Malformed is a 400 and never silently ignored. */
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

/** ADD, MOD and DEL as a reader would describe them, matching ParameterHistoryModal's wording. */
export const REVISION_TYPE_LABELS: Record<RevisionType, { text: string; color: 'blue' | 'orange' | 'grey' }> = {
  ADD: { text: 'Created', color: 'blue' },
  MOD: { text: 'Changed', color: 'orange' },
  DEL: { text: 'Removed', color: 'grey' }
};

/**
 * Field names the humaniser below would get wrong, and only those.
 *
 * Deliberately not a complete list of every audited field: the point of the diff contract is that
 * a field added to a class or an instructor shows up here without this file being edited, and a
 * map that had to be exhaustive would quietly break that promise the day it fell behind. So this
 * holds the acronyms and the pairs a reader could confuse, and everything else is derived.
 *
 * The operator-facing names come from exportKlassData in model/Klass.ts, which is keyed the other
 * way round (label -> value) and so cannot be reused directly.
 */
const FIELD_LABELS: Record<string, string> = {
  // Acronyms and product names a title-caser mangles
  tss: 'TSS',
  rhnId: 'RHN ID',
  rhcx: 'RHCX',
  rhscId: 'RHSC ID',
  rhlsLiveClass: 'RHLS live class',
  rhlsPremiumDelegates: 'RHLS premium delegates',
  rhlsCourseDelegates: 'RHLS course delegates',
  rhciAttendanceDate: 'RHCI attendance date',
  rhciVirtualAttendanceDate: 'RHCI virtual attendance date',
  nameLMS: 'Name on LMS',
  spocName: 'SPOC name',
  spocEmail: 'SPOC email',
  spocPhone: 'SPOC phone',
  spocConnected: 'SPOC connected',
  spocResponse: 'SPOC response',
  participantsRHNID: 'Participant RHN IDs',
  vtLabsRequired: 'VT labs required',
  niitCaseId: 'NIIT case id',
  certificationId: 'Certification #',
  passportNumber: 'Passport #',
  redhatEmail: 'Red Hat email',

  // Pairs a reader has to be careful with: these two are different dates and the
  // difference is the whole reason both are spelt out.
  dateCreated: 'Created in CAS',
  offeringCreated: 'Offering raised in LMS',

  // Spellings the source got wrong, corrected on the way to the reader only
  nacionality: 'Nationality',
  guarantedToRun: 'Guaranteed to run',
  passportAuthoriCountry: 'Passport authorising country',

  // Wordings the derived name reads badly for
  goNogoDate: 'Go / No-go date',
  languageChangeRequest: 'Language change requested',
  startDateTime: 'Start',
  endDateTime: 'End'
};

/**
 * A field name as a reader should see it: `deliveryResponsibility` reads "Delivery
 * responsibility", `rhlsLiveClass` reads "RHLS live class".
 *
 * Unknown fields are humanised rather than dropped, which is what keeps the contract's promise
 * that a new field on the service appears here for free — badly labelled beats invisible.
 */
export const fieldLabel = (field: string): string => {
  const known = FIELD_LABELS[field];
  if (known) return known;

  const spaced = field
    // camelCase -> camel Case, but not in the middle of an acronym
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim();

  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The fields a revision touched, as one line: "Course, Instructors and 5 more".
 *
 * Needed because the ADD revision of a class carries every field it was created with — 46 of them
 * on the ones in the dev database — so a collapsed row has to summarise rather than list. Two
 * names then a count, which is enough to decide whether to open it.
 */
export const changeSummary = (revision: RevisionEntry, namesShown = 2): string => {
  const count = revision.changes.length;

  if (count === 0) {
    return revision.type === 'DEL'
      // A delete row holds the identity and nothing else: the audit schema does not store values
      // at delete, so there is genuinely nothing to report beyond the event.
      ? 'no values recorded'
      : 'no audited field changed';
  }

  if (revision.type === 'ADD') return `${count} ${count === 1 ? 'field' : 'fields'} set`;

  const names = revision.changes.slice(0, namesShown).map(change => fieldLabel(change.field));
  const rest = count - names.length;

  return rest > 0 ? `${names.join(', ')} and ${rest} more` : names.join(', ');
};

/** True when the value is a Ref rather than a scalar — the one narrowing the renderers need. */
export const isRef = (value: unknown): value is Ref =>
  typeof value === 'object' && value !== null && 'id' in (value as Record<string, unknown>);
